import { useEffect, useState } from "react";
import { Satellite } from "lucide-react";
import { EarthScene } from "./components/EarthScene";
import { LiveReport } from "./components/LiveReport";
import { PhenomenonPanel } from "./components/PhenomenonPanel";
import { getEarthDashboardSnapshot } from "./services/earthData";
import type { TelemetrySnapshot } from "./types/telemetry";

export function App() {
  const [snapshot, setSnapshot] = useState<TelemetrySnapshot | null>(null);
  const [isLoadingLiveData, setIsLoadingLiveData] = useState(true);

  useEffect(() => {
    let isMounted = true;
    let intervalId = 0;

    const loadSnapshot = () => {
      getEarthDashboardSnapshot()
        .then((nextSnapshot) => {
          if (isMounted) {
            setSnapshot(nextSnapshot);
          }
        })
        .finally(() => {
          if (isMounted) {
            setIsLoadingLiveData(false);
          }
        });
    };

    loadSnapshot();
    intervalId = window.setInterval(loadSnapshot, 60_000);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, []);

  return (
    <main className="dashboard-shell">
      <div className="starfield" />
      <section className="earth-stage" aria-label="リアルタイム地球ダッシュボード">
        <header className="topbar">
          <div className="brand-lockup">
            <div className="brand-mark">
              <Satellite size={20} aria-hidden="true" />
            </div>
            <div>
              <p>今この瞬間の地球を宇宙から眺める</p>
              <div className="title-row">
                <h1>地球ライブ</h1>
                <span className="status-strip">
                  {isLoadingLiveData || !snapshot ? "地球データ確認中" : `USGS・ISS接続中 / ${snapshot.updatedAt}`}
                </span>
              </div>
            </div>
          </div>
        </header>

        {snapshot ? (
          <div className="mission-grid">
            <section className="globe-panel">
              <EarthScene
                observations={snapshot.globeObservations}
                animalTracks={snapshot.animalTracks}
              />
              <div className="globe-feed" aria-label="流れる観測メモ">
                <div className="globe-feed-track">
                  {[...snapshot.globeFeed, ...snapshot.globeFeed].map((item, index) => (
                    <span key={`${item}-${index}`}>{item}</span>
                  ))}
                </div>
              </div>
            </section>

            <aside className="right-rail" aria-label="今日の地球">
              <div className="rail-header">
                <span>今日の地球</span>
                <small>上の表示のメモ</small>
              </div>
              <PhenomenonPanel items={snapshot.phenomena} animals={snapshot.animalTracks} />
            </aside>
          </div>
        ) : (
          <div className="mission-grid mission-grid-loading">
            <section className="globe-panel globe-panel-loading">
              <p>地球データを確認中</p>
            </section>
          </div>
        )}
      </section>

      {snapshot && <LiveReport reports={snapshot.liveReport} />}
    </main>
  );
}
